// frontend/src/api/platformClient.js
//
// Talking to the /api/v1/platform/* superadmin API. Deliberately a SEPARATE
// module from api/client.js with its OWN localStorage token key
// ('platform_token', not 'token'), mirroring the backend split: a platform
// session is a different identity from a tenant session (see
// worker/lib/platformAuth.js). Keeping the keys separate means:
//   - a signed-in hotel user and a signed-in platform operator can coexist
//     in the same browser without clobbering each other,
//   - platformLogout() never touches the tenant session and vice versa,
//   - api/client.js's apiFetch (which auto-attaches the 'token' value) can
//     never accidentally send a platform token to a tenant route.
//
// Same base-URL resolution as api/client.js — REACT_APP_API_URL already
// ends in /api/v1, so paths here are bare (e.g. 'platform/me').
import { API_BASE } from './client';

const PLATFORM_TOKEN_KEY = 'platform_token';

export function getPlatformToken() {
  return localStorage.getItem(PLATFORM_TOKEN_KEY);
}
export function setPlatformToken(token) {
  localStorage.setItem(PLATFORM_TOKEN_KEY, token);
}
export function clearPlatformToken() {
  localStorage.removeItem(PLATFORM_TOKEN_KEY);
}

function buildUrl(path) {
  if (!API_BASE) return path;
  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

// Thin fetch wrapper: resolves `path`, attaches the platform Bearer token
// when present (unless the caller passed their own Authorization header),
// returns the raw Response. Callers do their own .json()/status checks,
// matching api/client.js's apiFetch contract.
export async function platformFetch(path, options = {}) {
  const url = buildUrl(path);
  const token = getPlatformToken();
  const headers = { ...(options.headers || {}) };
  if (token && !('Authorization' in headers)) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
}

// Convenience: platformFetch + JSON parse + a thrown Error on !ok, for the
// many call sites that just want the parsed body or an error to catch.
export async function platformJson(path, options = {}) {
  const res = await platformFetch(path, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const e = new Error(data.message || `Request failed (${res.status})`);
    e.status = res.status;
    e.code = data.code;
    throw e;
  }
  return data;
}
